/**
 * DevicesTableRow - Linha da tabela de dispositivos
 * Memoizado para evitar re-renders desnecessários
 */

import React, { memo } from 'react';
import {
  TableRow,
  TableCell,
  Checkbox,
  Box,
  Typography,
  Avatar,
  Chip,
  Tooltip,
} from '@mui/material';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import TwoWheelerIcon from '@mui/icons-material/TwoWheeler';
import AirportShuttleIcon from '@mui/icons-material/AirportShuttle';
import AgricultureIcon from '@mui/icons-material/Agriculture';
import LinkIcon from '@mui/icons-material/Link';
import dayjs from 'dayjs';

import { usePremiumTokens } from '../../common/theme/usePremiumTokens';
import { useTranslation } from '../../common/components/LocalizationProvider';
import { formatTimeAgo, getOfflineColorClass } from '../../common/util/timeUtils';
import DevicesIndicators from './DevicesIndicators';
import DeviceUsersValue from './DeviceUsersValue';
import CollectionActions from './CollectionActions';

// Category icons mapping
const CATEGORY_ICONS = {
  car: DirectionsCarIcon,
  truck: LocalShippingIcon,
  motorcycle: TwoWheelerIcon,
  bus: AirportShuttleIcon,
  tractor: AgricultureIcon,
  default: DirectionsCarIcon,
};

// Helper para texto curto de status
const getStatusLabel = (status) => {
  switch (status) {
    case 'online': return 'Online';
    case 'offline': return 'Offline';
    case 'unknown': return 'N/A';
    default: return '-';
  }
};

// Helper para cores do status - USA TOKENS DINÂMICOS
const getStatusColors = (status, tokens) => {
  const colors = {
    online: {
      text: tokens?.status?.online,
      bg: tokens?.status?.onlineBg,
      border: `${tokens?.status?.online}40`,
    },
    offline: {
      text: tokens?.status?.offline,
      bg: tokens?.status?.offlineBg,
      border: `${tokens?.status?.offline}40`,
    },
    unknown: {
      text: tokens?.status?.idle,
      bg: tokens?.status?.idleBg,
      border: `${tokens?.status?.idle}40`,
    },
  };

  return colors[status] || colors.unknown;
};

const DevicesTableRow = memo(({
  row,
  selected,
  onSelect,
  onRefresh,
  onNavigate,
  showUsers,
}) => {
  const t = useTranslation();
  const tokens = usePremiumTokens();

  const CategoryIcon = CATEGORY_ICONS[row.category] || CATEGORY_ICONS.default;
  const statusColors = getStatusColors(row.status, tokens);

  return (
    <TableRow
      hover
      selected={selected}
      sx={{
        cursor: 'pointer',
        transition: tokens?.transitions?.colors || 'all 0.15s ease',
        '&:hover': {
          backgroundColor: tokens?.surface?.hover || 'rgba(255,255,255,0.03)',
        },
        '&.Mui-selected': {
          backgroundColor: `${tokens?.accent?.primary || '#00d4aa'}08`,
          '&:hover': {
            backgroundColor: `${tokens?.accent?.primary || '#00d4aa'}12`,
          },
        },
      }}
    >
      {/* Checkbox */}
      <TableCell padding="checkbox" sx={{ py: 1, pl: 1, pr: 0, width: 36 }}>
        <Checkbox
          checked={selected}
          onChange={() => onSelect(row.id)}
          onClick={(e) => e.stopPropagation()}
          size="small"
          sx={{
            p: 0.5,
            color: tokens?.text?.tertiary,
            '&.Mui-checked': { color: tokens?.accent?.primary },
          }}
        />
      </TableCell>

      {/* Category Icon */}
      <TableCell padding="none" sx={{ py: 1, px: 0.25, width: 36 }}>
        <Avatar
          sx={{
            width: 28,
            height: 28,
            bgcolor: `${statusColors.text}15`,
          }}
        >
          <CategoryIcon sx={{ fontSize: 16, color: statusColors.text }} />
        </Avatar>
      </TableCell>

      {/* ID */}
      <TableCell sx={{ py: 1, pl: 0.5, pr: 1, width: 50 }}>
        <Typography variant="body2" sx={{ color: tokens?.text?.muted, fontFamily: 'monospace', fontSize: '0.8rem' }}>
          {row.id}
        </Typography>
      </TableCell>

      {/* Name + UniqueId */}
      <TableCell sx={{ py: 1, px: 1, minWidth: 180, maxWidth: 280 }}>
        <Box sx={{ overflow: 'hidden' }}>
          <Typography
            variant="body2"
            fontWeight={600}
            noWrap
            sx={{ fontSize: '0.875rem', textOverflow: 'ellipsis' }}
          >
            {row.name}
          </Typography>
          <Typography
            variant="caption"
            noWrap
            sx={{ color: tokens?.text?.muted, fontFamily: 'monospace', fontSize: '0.7rem', display: 'block' }}
          >
            {row.uniqueId}
          </Typography>
        </Box>
      </TableCell>

      {/* Group */}
      <TableCell sx={{ py: 1, px: 1, width: 90 }}>
        <Typography variant="body2" color="text.secondary" noWrap sx={{ fontSize: '0.8rem', textOverflow: 'ellipsis' }}>
          {row.groupName || '-'}
        </Typography>
      </TableCell>

      {/* Driver */}
      <TableCell sx={{ py: 1, px: 1, width: 100 }}>
        <Typography variant="body2" color="text.secondary" noWrap sx={{ fontSize: '0.8rem', textOverflow: 'ellipsis' }}>
          {row.driverName || '-'}
        </Typography>
      </TableCell>

      {/* Plate */}
      <TableCell sx={{ py: 1, px: 1, width: 110 }}>
        <Typography variant="body2" noWrap sx={{ fontSize: '0.8rem' }}>
          {row.attributes?.form?.plate || row.plate || '-'}
        </Typography>
      </TableCell>

      {/* Status + Indicators */}
      <TableCell align="center" sx={{ py: 1, px: 0.5, width: 170 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
          <Chip
            label={getStatusLabel(row.status)}
            size="small"
            className={`status-${row.status || 'unknown'}`}
            sx={{
              minWidth: 52,
              height: 22,
              fontSize: '0.65rem',
              fontWeight: 600,
              backgroundColor: statusColors.bg,
              color: statusColors.text,
              border: `1px solid ${statusColors.border}`,
              '& .MuiChip-label': { px: 0.75 },
            }}
          />
          <DevicesIndicators
            ignition={row.ignition}
            blocked={row.blocked}
            speed={row.speed}
            alarm={row.alarm}
            compact
          />
        </Box>
      </TableCell>

      {/* Last Update */}
      <TableCell sx={{ py: 1, px: 1, width: 85 }}>
        <Tooltip title={row.lastUpdate ? dayjs(row.lastUpdate).format('DD/MM/YYYY HH:mm:ss') : '-'}>
          <Typography
            variant="body2"
            noWrap
            className={getOfflineColorClass(row.lastUpdate)}
            sx={{
              fontWeight: 500,
              fontSize: '0.75rem',
            }}
          >
            {formatTimeAgo(row.lastUpdate)}
          </Typography>
        </Tooltip>
      </TableCell>

      {/* Users (manager only) */}
      {showUsers && (
        <TableCell align="center" sx={{ py: 1, px: 0.5, width: 50 }}>
          <DeviceUsersValue deviceId={row.id} />
        </TableCell>
      )}

      {/* Actions */}
      <TableCell align="right" sx={{ py: 1, pr: 1, width: 80 }}>
        <CollectionActions
          itemId={row.id}
          editPath="/settings/device"
          endpoint="devices"
          setTimestamp={onRefresh}
          compact
          customActions={[
            {
              key: 'connections',
              title: t('sharedConnections'),
              icon: <LinkIcon />,
              variant: 'warning',
              handler: (id) => onNavigate(`/settings/device/${id}/connections`),
            },
          ]}
        />
      </TableCell>
    </TableRow>
  );
});

DevicesTableRow.displayName = 'DevicesTableRow';

export default DevicesTableRow;
